import { Button, Col, Row } from 'react-bootstrap';
import { Email, EmailTypeEnum, Provider, ProviderTypeEnum } from '@/models/gen/graphql';
import { Validation, createNotification, getBulkValues, queryInput } from '@/utils';
import { createClientWithEmail, updateClientWithEmail } from '@/api/services/providers';
import { useEffect, useMemo, useState } from 'react';

import EditModal from '@/components/EditModal/new';
import FormField from '@/components/FormField';
import { GetStatesDocument } from '@/api/queries/';
import Logger from '@/utils/logs';
import PhoneInput from '@/components/MaskedInput/PhoneInput';
import SelectFromEnum from '@/components/SelectFromEnum';
import SelectInvoiceTerms from '@/components/SelectInvoiceTerms';
import SelectState from '@/components/SelectState';
import { Toast } from '@/models';
import { createClientValidator } from '@/api/services/providers/createClientBulk';
import useDb from '@/hooks/useDb';
import useForm from '@/hooks/useForm';
import { useSearchClients } from '@/api/services/providers/searchProviders';
import useValidation from '@/hooks/useValidation';

const log = Logger.of('EditClientsModal');

interface Props {
  show: boolean;
  title?: string;
  name: string;
  onHide?: Function;
  onSubmit?: (updates: any, selected: Provider[]) => void;
  onExited?: Function;
  onCancel?: Function;
  data: any;
  options?: {
    selected?: Provider[];
  };
  loading?: boolean;
}

type EditClientsState = Partial<Provider> & {
  billingSameAsPrimary?: boolean;
  newEmail?: string;
  newEmailType?: string;
};

// TOOD: remove this
const initState: EditClientsState = { type: ProviderTypeEnum.Client }; // the state needs to be an object for useValidation to work

const EditClientsModal = ({ show, onHide, onSubmit, onCancel, onExited, data: initData, options = {}, ...props }: Props): JSX.Element => {
  const [form, onChange, setForm] = useForm<EditClientsState>(initState);
  const [validForm, validity] = useValidation(createClientValidator, form);
  const [loadingNormalization, setLoadingNormalization] = useState<string | boolean>(false);
  const [normalizationFailed, setNormalizationFailed] = useState<Record<string, boolean>>({
    primary: false,
    billing: false,
  });
  const isPrimaryRequired = useMemo(
    () =>
      (form?.primaryAddress || '').length > 0 ||
      (form?.primaryCityCode || '').length > 0 ||
      (form?.primaryStateCode || '').length > 0 ||
      `${form?.primaryZipCode || ''}`.length > 0,
    [form]
  );
  const isBillingRequired = useMemo(
    () =>
      (form?.billingAddress || '').length > 0 ||
      (form?.billingCityCode || '').length > 0 ||
      (form?.billingStateCode || '').length > 0 ||
      `${form?.billingZipCode || ''}`.length > 0,
    [form]
  );
  const isValid = useMemo((): boolean => {
    let valid = !!validForm;
    // const validPrimaryAddress = ![
    //   (form?.primaryAddress || '').length > 0,
    //   (form?.primaryCityCode || '').length > 0,
    //   (form?.primaryStateCode || '').length > 0,
    //   `${form?.primaryZipCode || ''}`.length === 5,
    // ].includes(false);
    // const validBillingAddress = ![
    //   (form?.billingAddress || '').length > 0,
    //   (form?.billingCityCode || '').length > 0,
    //   (form?.billingStateCode || '').length > 0,
    //   `${form?.billingZipCode || ''}`.length === 5,
    // ].includes(false);
    // if (isPrimaryRequired && !validPrimaryAddress) valid = false;
    // if (isBillingRequired && !validBillingAddress) valid = false;
    // if (normalizationFailed.primary || normalizationFailed.billing) valid = false;
    return valid;
  }, [validForm, form, validity, normalizationFailed, isPrimaryRequired, isBillingRequired]);

  const [{ data, loading: loadingClients }, { refetch: searchClients }] = useSearchClients();
  const loading = props?.loading || loadingClients || loadingNormalization;
  const provider = useMemo((): Provider => data?.rows?.[0], [data]);

  const mode = initData?.id ? 'edit' : 'create';
  const selected = options?.selected ? options?.selected : provider ? [provider] : [];
  const selectedCount = selected?.length || 0;
  const fields = useMemo(() => (mode === 'edit' ? getBulkValues(selected) : undefined), [selected, mode]);
  const { data: states } = useDb('States', GetStatesDocument);
  const statesReverseLookup = useMemo(
    () =>
      (states?.getFilters?.filters?.states || []).reduce((acc, curr) => {
        acc[curr.id] = curr.id;
        acc[curr.displayName] = curr.id;
        return acc;
      }, {}),
    [states]
  );

  const handleSubmit = async (): Promise<void> => {
    try {
      if (onSubmit) await onSubmit(form, selected);
      else {
        if (mode === 'edit') await updateClientWithEmail(form, selected);
        else await createClientWithEmail(form);
      }
    } catch (err) {
      log.error('handleSubmit', err);
    } finally {
      await onHide();
    }
  };

  const handleSyncPrimaryAndBilling = (event: any): void => {
    const { name, value } = event.target;
    onChange({ target: { name, value } });
    if (form?.billingSameAsPrimary) {
      setForm((current: EditClientsState): EditClientsState => ({ ...current, [name.replace('primary', 'billing')]: value }));
    }
  };

  const handleAddEmail = (newEmailType: string): void => {
    // TODO: Add mutation to add email to the location.
    if (!form?.newEmail) return; // TODO: Add Invalid logic
    if (!newEmailType) return; // TODO: Add Invalid logic
    setForm(
      (current: EditClientsState): EditClientsState => ({
        ...current,
        emails: [...(current.emails || []), { address: form?.newEmail, type: newEmailType } as Email],
        newEmail: '',
        newEmailType: '',
      })
    );
  };

  const handleRemoveEmail = (index: number): void => {
    setForm(
      (current: EditClientsState): EditClientsState => ({
        ...current,
        emails: current.emails.filter((email: Email, a: number): boolean => a !== index),
      })
    );
  };
  // const cachedAddresses = useRef<Record<string, NormalizedAddress>>({});
  // const handleNormalization =
  //   (isBilling: boolean = false): (() => Promise<void>) =>
  //   async (): Promise<void> => {
  //     const address = isBilling ? form?.billingAddress : form?.primaryAddress;
  //     if (!address) {
  //       setLoadingNormalization(false);
  //       setNormalizationFailed((current) => ({ ...current, ...(isBilling ? { billing: false } : { primary: false }) }));
  //       return;
  //     }
  //     let res = {} as NormalizedAddress;
  //     setLoadingNormalization(true);
  //     try {
  //       const cachedAddress = cachedAddresses.current[address] || ({} as NormalizedAddress);
  //       if (!cachedAddress.address || !cachedAddress.cityName || !cachedAddress.stateCode || !cachedAddress.zipCode) {
  //         res = await getNormalizedAddress({ address });
  //         if (res.stateCode.length > 2) res.stateCode = undefined;
  //       } else {
  //         res = cachedAddresses.current[address];
  //       }
  //       setNormalizationFailed((current) => ({ ...current, ...(isBilling ? { billing: false } : { primary: false }) }));
  //     } catch (err) {
  //       console.error(err);
  //       res = {} as NormalizedAddress;
  //       setNormalizationFailed((current) => ({ ...current, ...(isBilling ? { billing: true } : { primary: true }) }));
  //     } finally {
  //       cachedAddresses.current[res?.address || address] = res;
  //       cachedAddresses.current[address] = res;
  //       setForm(
  //         (current: EditClientsState): EditClientsState => ({
  //           ...current,
  //           ...(isBilling
  //             ? {
  //                 billingAddress: res?.address || current?.billingAddress,
  //                 billingCityCode: res?.cityName || current?.billingCityCode,
  //                 billingStateCode: statesReverseLookup[res?.stateCode] || res?.stateCode || current?.billingStateCode,
  //                 billingZipCode: parseInt(`${res?.zipCode || current?.billingZipCode}`),
  //               }
  //             : {
  //                 primaryAddress: res?.address || current?.primaryAddress,
  //                 primaryCityCode: res?.cityName || current?.primaryCityCode,
  //                 primaryStateCode: statesReverseLookup[res?.stateCode] || res?.stateCode || current?.primaryStateCode,
  //                 primaryZipCode: parseInt(`${res?.zipCode || current?.primaryZipCode}`),
  //                 ...(form?.billingSameAsPrimary
  //                   ? {
  //                       billingAddress: res?.address || current?.billingAddress,
  //                       billingCityCode: res?.cityName || current?.billingCityCode,
  //                       billingStateCode: res?.stateCode || current?.billingStateCode,
  //                       billingZipCode: parseInt(`${res?.zipCode || current?.billingZipCode}`),
  //                     }
  //                   : {}),
  //               }),
  //         })
  //       );
  //       setLoadingNormalization(false);
  //     }
  //   };

  useEffect((): void => {
    if (form?.billingSameAsPrimary) {
      setForm(
        (current: EditClientsState): EditClientsState => ({
          ...current,
          billingAddress: current?.primaryAddress,
          billingCityCode: current?.primaryCityCode,
          billingStateCode: current?.primaryStateCode,
          billingZipCode: current?.primaryZipCode,
        })
      );
    }
  }, [form?.billingSameAsPrimary]);

  const handleSearch = async (): Promise<void> => {
    if (!initData?.id || !show) return;
    const res = await searchClients([{ id: queryInput(initData?.id) }]);
    const provider = res?.rows?.[0];
    if (!provider) return createNotification('Provider not found.', Toast.Type.WARNING, 'Search Client');
    setForm(provider);
  };

  useEffect((): void => {
    if (show) {
      handleSearch();
    } else {
      setForm(initState);
    }
  }, [show]);

  const title = props?.title || mode === 'edit' ? 'Edit Client' : 'Create Client';

  return (
    <EditModal
      show={show}
      title={title}
      icon="fa fa-location-dot"
      size="xl"
      onHide={onHide}
      onCancel={onCancel}
      onExited={onExited}
      onSubmit={isValid && !loading ? handleSubmit : false}
      options={undefined}
      loading={props?.loading || loadingClients}
      name="editClients"
    >
      <Row className="EditClients mt-4">
        <Row>
          <Col md={4}>
            <FormField
              name="name"
              label="Legal Name:"
              value={form?.name || ''}
              onChange={onChange}
              valid={validity?.name?.valid}
              placeholder="Legal Name"
              feedback={validity?.name?.message}
              disabled={(fields?.name || []).length > 1}
              required
            />
            <FormField
              type="email"
              name="primaryEmail"
              placeholder="Email"
              label="Email:"
              value={form?.primaryEmail || ''}
              onChange={onChange}
              disabled={selectedCount > 1}
              valid={validity?.primaryEmail?.valid}
            />
            <FormField
              name="primaryPhoneNumber"
              label="Phone:"
              value={form?.primaryPhoneNumber || ''}
              onChange={(value) => onChange({ target: { name: 'primaryPhoneNumber', value } })}
              valid={validity?.primaryPhoneNumber?.valid}
              placeholder="Phone"
              feedback={validity?.primaryPhoneNumber?.message}
              disabled={(fields?.primaryPhoneNumber || []).length > 1}
              options={{
                input: {
                  as: PhoneInput,
                },
              }}
            />
            <FormField
              name="invoiceTermId"
              label="Invoice Terms:"
              value={form?.invoiceTermId || ''}
              onChange={onChange}
              valid={validity?.invoiceTermId?.valid}
              placeholder="Invoice Terms"
              feedback={validity?.invoiceTermId?.message}
              disabled={(fields?.invoiceTermId || []).length > 1}
              options={{
                input: {
                  as: SelectInvoiceTerms,
                },
              }}
            />
          </Col>
          <Col md={4}>
            <FormField
              name="displayName"
              label="Shortname:"
              value={form?.displayName || ''}
              onChange={onChange}
              valid={validity?.displayName?.valid}
              placeholder="Shortname"
              feedback={validity?.displayName?.message}
              disabled={(fields?.displayName || []).length > 1}
              required
            />
            <FormField
              name="referenceNumber"
              label="Ref ID:"
              value={form?.referenceNumber || ''}
              onChange={onChange}
              valid={validity?.referenceNumber?.valid}
              placeholder="Ref ID"
              feedback={validity?.referenceNumber?.message}
              disabled={(fields?.referenceNumber || []).length > 1}
            />
            <FormField
              name="occPhoneNumber"
              label="Occ Phone:"
              value={form?.occPhoneNumber || ''}
              onChange={(value) => onChange({ target: { name: 'occPhoneNumber', value } })}
              valid={validity?.occPhoneNumber?.valid}
              placeholder="Occ Phone"
              feedback={validity?.occPhoneNumber?.message}
              disabled={(fields?.occPhoneNumber || []).length > 1}
              options={{
                input: {
                  as: PhoneInput,
                },
              }}
            />
          </Col>
          <Col md={4}>
            <FormField
              name="contactPerson"
              label="Contact Person:"
              value={form?.contactPerson || ''}
              onChange={onChange}
              valid={validity?.contactPerson?.valid}
              placeholder="Contact Person"
              feedback={validity?.contactPerson?.message}
              disabled={(fields?.contactPerson || []).length > 1}
            />
            <FormField
              name="mobilePhoneNumber"
              label="Mobile Phone:"
              value={form?.mobilePhoneNumber || ''}
              onChange={(value) => onChange({ target: { name: 'mobilePhoneNumber', value } })}
              valid={validity?.mobilePhoneNumber?.valid}
              placeholder="Mobile Phone"
              feedback={validity?.mobilePhoneNumber?.message}
              disabled={(fields?.mobilePhoneNumber || []).length > 1}
              options={{
                input: {
                  as: PhoneInput,
                },
              }}
            />
            <FormField
              name="faxNumber"
              label="Fax:"
              value={form?.faxNumber || ''}
              onChange={(value) => onChange({ target: { name: 'faxNumber', value } })}
              valid={validity?.faxNumber?.valid}
              placeholder="Fax"
              feedback={validity?.faxNumber?.message}
              disabled={(fields?.faxNumber || []).length > 1}
              options={{
                input: {
                  as: PhoneInput,
                },
              }}
            />
          </Col>
        </Row>
      </Row>
      <Row className="mt-4">
        <Col md={6} className="d-flex align-items-center">
          <h5>
            <strong>Primary Address</strong>
          </h5>
        </Col>
        <Col md={6} className="pe-0 d-flex align-items-center justify-content-between">
          <h5>
            <strong>Billing Address</strong>
          </h5>

          <div
            className="bg-gray bg-opacity-10 d-flex align-items-center justify-content-center"
            style={{ borderRadius: '1.5rem 1.5rem 0 0', padding: '1rem 1rem .75rem 1rem' }}
          >
            <input name="billingSameAsPrimary" type="checkbox" checked={form?.billingSameAsPrimary || false} onChange={onChange.toggle} />
            <div className="ps-1">Same As Primary Address</div>
          </div>
        </Col>
      </Row>
      <Row>
        <Row className="bg-gray bg-opacity-10 g-0 {border-radius:1.5rem|0|1.5rem|1.5rem;}">
          <Col md={6} className="p-3">
            <Row style={{ borderRight: '1px solid black' }}>
              <Row>
                <Col>
                  <FormField
                    name="primaryAddress"
                    label="Street Address:"
                    value={form?.primaryAddress || ''}
                    onChange={handleSyncPrimaryAndBilling}
                    // onBlur={handleNormalization(false)}
                    // valid={
                    //   loadingNormalization === 'primary' || !isPrimaryRequired
                    //     ? undefined
                    //     : normalizationFailed.primary
                    //       ? false
                    //       : (form?.primaryAddress || '')?.length > 0
                    // }
                    placeholder="Street Address"
                    feedback={validity?.primaryAddress?.message}
                    disabled={(fields?.primaryAddress || []).length > 1}
                    // onFocus={() => setLoadingNormalization('primary')}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="pe-0">
                  <FormField
                    name="primaryCityCode"
                    label="City:"
                    value={form?.primaryCityCode || ''}
                    onChange={handleSyncPrimaryAndBilling}
                    // valid={!isPrimaryRequired ? undefined : form?.primaryCityCode?.length > 0}
                    placeholder="City"
                    feedback={validity?.primaryCityCode?.message}
                    disabled={(fields?.primaryCityCode || []).length > 1}
                  />
                </Col>
                <Col className="pe-0">
                  <FormField
                    name="primaryStateCode"
                    label="State:"
                    value={form?.primaryStateCode || ''}
                    onChange={handleSyncPrimaryAndBilling}
                    // valid={!isPrimaryRequired ? undefined : form?.primaryStateCode?.length > 0}
                    placeholder="--"
                    feedback={validity?.primaryStateCode?.message}
                    disabled={(fields?.primaryStateCode || []).length > 1}
                    options={{
                      input: {
                        as: SelectState,
                      },
                    }}
                  />
                </Col>
                <Col>
                  <FormField
                    name="primaryZipCode"
                    label="Zip:"
                    type="number"
                    value={form?.primaryZipCode || ''}
                    onChange={(event) =>
                      handleSyncPrimaryAndBilling({
                        target: { name: 'primaryZipCode', value: event.target.value !== '' ? parseInt(event.target.value) : null },
                      })
                    }
                    // valid={!isPrimaryRequired ? undefined : `${form?.primaryZipCode || ''}`.length === 5}
                    placeholder="Zip"
                    feedback={validity?.primaryZipCode?.message}
                    disabled={(fields?.primaryZipCode || []).length > 1}
                  />
                </Col>
              </Row>
            </Row>
          </Col>
          <Col md={6} className="p-3">
            <Row>
              <Row>
                <Col>
                  <FormField
                    name="billingAddress"
                    label="Street Address:"
                    value={form?.billingAddress || ''}
                    onChange={onChange}
                    // onBlur={handleNormalization(true)}
                    // valid={
                    //   loadingNormalization === 'billing' || !isBillingRequired
                    //     ? undefined
                    //     : normalizationFailed.billing
                    //       ? false
                    //       : form?.billingAddress?.length > 0
                    // }
                    placeholder="Street Address"
                    feedback={validity?.billingAddress?.message}
                    disabled={!!form?.billingSameAsPrimary || (fields?.billingAddress || []).length > 1}
                    // onFocus={() => setLoadingNormalization('billing')}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="pe-0">
                  <FormField
                    name="billingCityCode"
                    label="City:"
                    value={form?.billingCityCode || ''}
                    onChange={onChange}
                    // valid={!isBillingRequired ? undefined : form?.billingCityCode?.length > 0}
                    placeholder="City"
                    feedback={validity?.billingCityCode?.message}
                    disabled={!!form?.billingSameAsPrimary || (fields?.billingCityCode || []).length > 1}
                  />
                </Col>
                <Col className="pe-0">
                  <FormField
                    name="billingStateCode"
                    label="State:"
                    value={form?.billingStateCode || ''}
                    onChange={onChange}
                    // valid={!isBillingRequired ? undefined : form?.billingStateCode?.length > 0}
                    placeholder="--"
                    feedback={validity?.billingStateCode?.message}
                    disabled={!!form?.billingSameAsPrimary || (fields?.billingStateCode || []).length > 1}
                    options={{
                      input: {
                        as: SelectState,
                      },
                    }}
                  />
                </Col>
                <Col>
                  <FormField
                    name="billingZipCode"
                    label="Zip:"
                    type="number"
                    value={form?.billingZipCode || ''}
                    onChange={(event) =>
                      onChange({
                        target: { name: 'billingZipCode', value: event.target.value !== '' ? parseInt(event.target.value) : null },
                      })
                    }
                    // valid={!isBillingRequired ? undefined : `${form?.billingZipCode || ''}`.length === 5}
                    placeholder="Zip"
                    feedback={validity?.billingZipCode?.message}
                    disabled={!!form?.billingSameAsPrimary || (fields?.billingZipCode || []).length > 1}
                  />
                </Col>
              </Row>
            </Row>
          </Col>
        </Row>
      </Row>
      {/* Display emails component */}
      <h3 className="{border-bottom:1px|solid|grey;padding:.7rem|0;}">Emails</h3>
      {form?.emails && (
        <div className="{overflow-x:hidden;max-height:180px;}">
          {(form?.emails || []).map(
            (email: Email, e: number): JSX.Element => (
              <Row className="mb-1 align-items-center" key={e}>
                <Col md={8}>
                  <FormField
                    label="Email:"
                    name={`emails.${e}.address`}
                    value={email?.address}
                    valid={Validation.isEmail(email?.address)}
                    placeholder="Email"
                    onChange={onChange}
                  />
                </Col>
                <Col md={3}>
                  <FormField
                    name={`emails.${e}.type`}
                    label="Type:"
                    value={email?.type}
                    onChange={onChange}
                    source={EmailTypeEnum}
                    placeholder="Role"
                    valid={!!email?.type}
                    disabled={selectedCount > 1}
                    options={{
                      feedback: { asTooltip: true },
                      input: {
                        as: SelectFromEnum,
                      },
                    }}
                  />
                </Col>
                <Col md={1}>
                  <Button
                    className="{margin-top:11px;}"
                    name="REMOVE_EMAIL"
                    variant="outline-danger"
                    onClick={(): void => handleRemoveEmail(e)}
                  >
                    <i className="fa fa-minus align-self-center justify-self-center" />
                  </Button>
                </Col>
              </Row>
            )
          )}
        </div>
      )}
      <Row className="align-items-center">
        <Col md={8}>
          <FormField
            type="email"
            label="Enter Email:"
            name="newEmail"
            placeholder="Enter Email Here"
            value={form?.newEmail || ''}
            onChange={onChange}
            disabled={selectedCount > 1}
            valid={Validation.isEmail(form?.newEmail) ? true : form?.newEmail ? false : undefined}
          />
        </Col>
        <Col md={3}>
          <FormField
            name="newEmailType"
            label="Type:"
            value={form?.newEmailType || ''}
            onChange={(e) => {
              onChange(e);
              handleAddEmail(e.target.value);
            }}
            source={EmailTypeEnum}
            placeholder="Role"
            valid={validity?.newEmailType?.valid}
            disabled={selectedCount > 1 || !Validation.isEmail(form?.newEmail)}
            options={{
              feedback: { asTooltip: true },
              input: {
                as: SelectFromEnum,
              },
            }}
          />
        </Col>
      </Row>
    </EditModal>
  );
};

export default EditClientsModal;
